import * as React from 'react';
import { MenuProduct } from '../services/MenuService.ts';
import ProductTileV3 from './ProductTileV3.tsx';
import './HomeCollections.css';

const { useMemo, useRef, useState, useCallback, useEffect } = React;

const HOME_COLLECTIONS = [
  { categoryKeys: ['flower', 'flowers'], displayName: 'Flower', categoryValue: 'flower' },
  { categoryKeys: ['edible', 'edibles'], displayName: 'Edibles', categoryValue: 'edible' },
  { categoryKeys: ['concentrate', 'concentrates'], displayName: 'Concentrates', categoryValue: 'concentrate' },
  { categoryKeys: ['vaporizer', 'vaporizers', 'vape', 'vapes', 'cartridge', 'cartridges'], displayName: 'Vapes', categoryValue: 'vape' },
];

interface ServerCollection {
  id: string;
  name: string;
  productIds: string[];
  shuffleProducts?: boolean;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

interface HomeCollectionsProps {
  products: MenuProduct[];
  pricingType: 'MEDICAL' | 'RECREATIONAL';
  menuId?: string;
  isMobile?: boolean;
  cart: any;
  cartLoading: boolean;
  addingToCart: Set<string>;
  activeMarquees: Set<string>;
  stoppingMarquees: Set<string>;
  dealDescriptions?: Record<string, { title: string; description: string }>;
  productTileCursorStyle?: React.CSSProperties;
  selectedTerpenes: string[];
  selectedCannabinoids: string[];
  debugMode?: boolean;
  serverCollections?: ServerCollection[] | null;
  onProductClick: (product: MenuProduct) => void;
  onProductHover: (productId: string) => void;
  onProductLeave: (productId: string) => void;
  onAddToCart: (product: MenuProduct, variant: any) => void;
  onDebugClick: (product: MenuProduct) => void;
  onDealClick: (product: MenuProduct) => void;
  onVermontIncrement?: (product: MenuProduct, variant: any) => void;
  onVermontDecrement?: (product: MenuProduct, variant: any) => void;
  onViewAllClick: (category: string) => void;
}

interface CollectionData {
  displayName: string;
  categoryValue: string;
  items: { product: MenuProduct; variant: any }[];
}

const HomeCollections: React.FC<HomeCollectionsProps> = ({
  products,
  pricingType,
  menuId,
  isMobile,
  cart,
  cartLoading,
  addingToCart,
  activeMarquees,
  stoppingMarquees,
  dealDescriptions,
  productTileCursorStyle,
  selectedTerpenes,
  selectedCannabinoids,
  debugMode,
  serverCollections,
  onProductClick,
  onProductHover,
  onProductLeave,
  onAddToCart,
  onDebugClick,
  onDealClick,
  onVermontIncrement,
  onVermontDecrement,
  onViewAllClick,
}) => {
  const scrollRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [scrollPositions, setScrollPositions] = useState<Map<string, { atStart: boolean; atEnd: boolean }>>(new Map());

  const useServerCollections = serverCollections && serverCollections.length > 0;

  const productMap = useMemo(() => {
    if (!useServerCollections) return null;
    const map = new Map<string, MenuProduct>();
    products.forEach(p => map.set(p.id, p));
    return map;
  }, [products, useServerCollections]);

  const collections = useMemo<CollectionData[]>(() => {
    if (useServerCollections && productMap) {
      // Server-configured collections: match product IDs against loaded products
      return serverCollections!.map(sc => {
        const items: { product: MenuProduct; variant: any }[] = [];
        for (const pid of sc.productIds) {
          const product = productMap.get(pid);
          if (product) {
            items.push({ product, variant: product.variants?.[0] || null });
          }
          // Stale product IDs are silently skipped
        }
        return {
          displayName: sc.name,
          categoryValue: sc.id, // Use collection ID as key
          items: sc.shuffleProducts ? shuffleArray(items) : items,
        };
      }).filter(c => c.items.length > 0);
    }

    // Fallback: hardcoded category-based collections
    return HOME_COLLECTIONS.map(({ categoryKeys, displayName, categoryValue }) => {
      const filtered = products.filter(p =>
        categoryKeys.includes(p.category?.toLowerCase())
      );
      const items = filtered.slice(0, 6).map(product => ({
        product,
        variant: product.variants?.[0] || null,
      }));
      return { displayName, categoryValue, items };
    }).filter(c => c.items.length > 0);
  }, [products, useServerCollections, serverCollections, productMap]);

  const updateScrollPosition = useCallback((key: string) => {
    const container = scrollRefs.current.get(key);
    if (!container) return;
    const atStart = container.scrollLeft <= 1;
    const atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
    setScrollPositions(prev => {
      const next = new Map(prev);
      next.set(key, { atStart, atEnd });
      return next;
    });
  }, []);

  const handleScroll = useCallback((key: string, direction: 'left' | 'right') => {
    const container = scrollRefs.current.get(key);
    if (!container) return;
    const tileWidth = container.querySelector('.home-collection-tile')?.clientWidth || 300;
    const gap = 16;
    container.scrollBy({ left: direction === 'right' ? tileWidth + gap : -(tileWidth + gap), behavior: 'smooth' });
  }, []);

  const setScrollRef = useCallback((key: string, el: HTMLDivElement | null) => {
    if (el) {
      scrollRefs.current.set(key, el);
    } else {
      scrollRefs.current.delete(key);
    }
  }, []);

  // Initialize and re-check scroll positions after collections change or mount
  useEffect(() => {
    // Small delay to ensure refs are attached and layout is complete
    const timer = setTimeout(() => {
      const next = new Map<string, { atStart: boolean; atEnd: boolean }>();
      collections.forEach(c => {
        const container = scrollRefs.current.get(c.categoryValue);
        if (container) {
          next.set(c.categoryValue, {
            atStart: container.scrollLeft <= 1,
            atEnd: container.scrollLeft + container.clientWidth >= container.scrollWidth - 1,
          });
        }
      });
      setScrollPositions(next);
    }, 50);
    return () => clearTimeout(timer);
  }, [collections]);

  return (
    <div className="home-collections">
      {collections.map(({ displayName, categoryValue, items }) => {
        const pos = scrollPositions.get(categoryValue) || { atStart: true, atEnd: true };
        const showArrows = items.length > 0;

        return (
          <div className="home-collection" key={categoryValue}>
            <div className="home-collection-header">
              <h2 className="home-collection-title">{displayName}</h2>
              <button
                className="home-collection-view-all home-collection-view-all-desktop"
                onClick={() => onViewAllClick(categoryValue)}
              >
                VIEW ALL
              </button>
            </div>

            <div className={`home-collection-scroll-wrapper${pos.atStart ? ' at-start' : ''}${pos.atEnd ? ' at-end' : ''}`}>
              {showArrows && !pos.atStart && (
                <button
                  className="home-collection-arrow home-collection-arrow-left"
                  onClick={() => handleScroll(categoryValue, 'left')}
                  aria-label="Scroll left"
                />
              )}

              <div
                className="home-collection-scroll"
                ref={(el) => setScrollRef(categoryValue, el)}
                onScroll={() => updateScrollPosition(categoryValue)}
              >
                {items.map(({ product, variant }) => {
                  const tileId = `${product.id}-${variant?.id || 'default'}`;
                  return (
                    <div className="home-collection-tile" key={tileId}>
                      <ProductTileV3
                        product={product}
                        variant={variant}
                        tileId={tileId}
                        animationClass=""
                        pricingType={pricingType}
                        menuId={menuId}
                        selectedCategory={categoryValue}
                        isVermontStore={menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC}
                        debugMode={debugMode}
                        cart={cart}
                        cartLoading={cartLoading}
                        isAddingToCart={addingToCart.has(`${product.id}-${variant?.id}`) || addingToCart.has(`vermont-${product.id}`)}
                        activeMarquees={activeMarquees}
                        stoppingMarquees={stoppingMarquees}
                        dealDescriptions={dealDescriptions}
                        productTileCursorStyle={productTileCursorStyle}
                        isMobile={isMobile}
                        selectedTerpenes={selectedTerpenes}
                        selectedCannabinoids={selectedCannabinoids}
                        onProductClick={onProductClick}
                        onProductHover={onProductHover}
                        onProductLeave={onProductLeave}
                        onAddToCart={onAddToCart}
                        onDebugClick={onDebugClick}
                        onDealClick={onDealClick}
                        onVermontIncrement={onVermontIncrement}
                        onVermontDecrement={onVermontDecrement}
                      />
                    </div>
                  );
                })}
              </div>

              {showArrows && !pos.atEnd && (
                <button
                  className="home-collection-arrow home-collection-arrow-right"
                  onClick={() => handleScroll(categoryValue, 'right')}
                  aria-label="Scroll right"
                />
              )}
            </div>

            <button
              className="home-collection-view-all home-collection-view-all-mobile"
              onClick={() => onViewAllClick(categoryValue)}
            >
              VIEW ALL
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default HomeCollections;
